import { useState, useEffect, useContext, useRef } from "react";
import { useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import MuiAutocomplete from "@mui/material/Autocomplete";
import { ComponentPropsContext, ComponentPropsContextApi } from "layouts/Flow Designer/context";
import AutocompletePopperComponent from "../AutocompletePopperComponent";
import { TextField } from "../StyledComponents";
import { getObjectPaths } from "helpers/common";
import { COMMON_COMPONENT_TYPES } from "constants/flowDesigner";
import SoftTypography from "components/SoftTypography";

const Autocomplete = styled(MuiAutocomplete)(() => ({
   "& .MuiAutocomplete-endAdornment": {
      display: "none"
   }
}));

const AutocompleteInput = ({
   action,
   fieldValue,
   onChange,
   isMultiline,
   keyValueInput,
   hasSecondaryInput,
   secondaryInputKey = "secondaryInput",
   className = "",
   isBranchInput,
   disabled,
   noValidate,
   hideLabel = false,
   onBlur,
   errorHelperText,
   literalOptions = []
}) => {
   const { actionForm, formErrors } = useContext(ComponentPropsContext);
   const { setActionForm, setFormErrors } = useContext(ComponentPropsContextApi);
   const [open, setOpen] = useState(false);
   const [dataSource, setDataSource] = useState([]);
   const { context } = useSelector((store) => store?.designer?.editorContext);
   const { selectedNode } = useSelector((store) => store?.designer?.componentPropsPanel);
   const [invalidEmailInput, setInvalidEmailInput] = useState(false);
   const inputRef = useRef(null);
   const allOptions = useRef([]);

   const closeAutocomplete = () => {
      setOpen(false);
   };

   const resolveContextPath = (path) => {
      const parts = path.split(".");
      const rootKey = parts[0];
      let current = null;

      if (rootKey === "steps") current = context?.steps;
      else if (rootKey === "triggers") current = context?.triggers;
      else if (rootKey === "settings") current = context?.settings;
      else if (rootKey === "variables") current = context?.variables;
      else if (rootKey === "flow_configuration") current = context?.flow_configuration;
      else if (rootKey === "shared_flow_configurations") current = context?.shared_flow_configurations;
      else if (rootKey === "invocation") current = context?.invocation;
      else if (rootKey === "session") current = context?.session;
      else return null;

      for (let i = 1; i < parts.length; i++) {
         if (current == null || typeof current !== "object") return null;
         current = current[parts[i]];
      }

      return current;
   };

   const getChildOptions = (atPath) => {
      const cleanPath = atPath.replace(/^@/, "");
      const resolved = resolveContextPath(cleanPath);

      if (resolved && typeof resolved === "object") {
         const childPaths = getObjectPaths(resolved);
         return childPaths.map((childPath) => `${atPath}.${childPath.join(".")}`);
      }

      const parts = cleanPath.split(".");
      const parentPath = parts.slice(0, -1).join(".");
      const parentResolved = parentPath ? resolveContextPath(parentPath) : null;

      if (parentResolved && typeof parentResolved === "object") {
         const siblingPaths = getObjectPaths(parentResolved);
         return siblingPaths.map((sibPath) => `@${parentPath}.${sibPath.join(".")}`);
      }

      return allOptions.current;
   };

   const getAtTokenAtCursor = (str, cursorPos) => {
      if (!str) return null;

      const textBeforeCursor = str.substring(0, cursorPos);
      const match = textBeforeCursor.match(/@[\w.]*$/);
      return match ? match[0] : null;
   };

   const handleFocus = () => {
      const input = inputRef.current?.querySelector("input");
      const value = input?.value || "";
      const cursorPos = input?.selectionStart ?? value.length;
      const atToken = getAtTokenAtCursor(value, cursorPos);

      if (atToken && atToken.includes(".")) {
         const childOptions = getChildOptions(atToken);
         setDataSource(childOptions);
         setOpen(true);
      } else if (atToken) {
         setDataSource(allOptions.current);
         setOpen(true);
      } else if (literalOptions.length) {
         setDataSource(literalOptions);
         setOpen(true);
      }
   };

   const handleAutocompleteChange = (e, value) => {
      if (!open) return;

      onChange(e, value);

      if (hasSecondaryInput) {
         const newActionForm = { ...actionForm };
         newActionForm[action.parentField] = {
            ...newActionForm[action.parentField],
            [secondaryInputKey]: {
               ...newActionForm[action.parentField]?.[secondaryInputKey],
               value,
               isVariable: true
            }
         };

         closeAutocomplete();
         return setActionForm(newActionForm);
      }

      if (keyValueInput) {
         let actionFormCopy = { ...actionForm };
         actionFormCopy = {
            ...actionForm,
            [action.field]: {
               ...actionForm[action.field],
               value: [...(actionForm[action.field].value?.map((item, index) => (index === keyValueInput.index ? { ...item, value: value, isVariable: true } : item)) ?? [])]
            }
         };
         setActionForm(actionFormCopy);
      } else {
         const input = inputRef.current?.querySelector("input");
         const cursorPos = input.selectionStart;
         const actionFormValue = actionForm[action.field]?.value;
         let newValue = "";
         let wordToReplace = "";

         wordToReplace = getNearestAtWord(actionForm[action.field]?.value, cursorPos);

         if (actionFormValue === value) {
            newValue = value;
         } else {
            if (isBranchInput) {
               const branchActionFormValue = actionForm?.branches?.find((branch) => branch.key === action.field);
               wordToReplace = getNearestAtWord(branchActionFormValue?.value, cursorPos);
               newValue = replaceLastMatch(branchActionFormValue?.value, wordToReplace, value);

               setActionForm({
                  ...actionForm,
                  branches: actionForm?.branches?.map((branch) => {
                     if (branch.key === action.field) {
                        return {
                           ...branch,
                           value: newValue,
                           isVariable: true
                        };
                     }
                     return branch;
                  })
               });
               closeAutocomplete();
               return;
            }
            newValue = replaceLastMatch(actionForm[action.field]?.value, wordToReplace, value);
         }

         setActionForm({
            ...actionForm,
            [action.field]: { ...actionForm[action.field], value: newValue, isVariable: true }
         });
      }
   };

   const replaceLastMatch = (input, pattern, replacement) => {
      const regex = new RegExp(pattern + "(?!.*" + pattern + ")");
      return input.replace(regex, replacement);
   };

   const getNearestAtWord = (str, cursorPosition) => {
      const wordsWithPositions = [];
      const regex = /@[\w.]+|@/g;
      let match;

      while ((match = regex.exec(str)) !== null) {
         wordsWithPositions.push({ word: match[0], index: match.index });
      }

      if (wordsWithPositions.length === 0) {
         return null;
      }

      let nearestWord = wordsWithPositions[0];
      let minDistance = Math.abs(cursorPosition - nearestWord.index);

      for (let i = 1; i < wordsWithPositions.length; i++) {
         const distance = Math.abs(cursorPosition - wordsWithPositions[i].index);
         if (distance < minDistance) {
            minDistance = distance;
            nearestWord = wordsWithPositions[i];
         }
      }

      return nearestWord.word;
   };

   const filterAutompleteOptions = (options, { inputValue }) => {
      const match = inputValue.match(/@([\w._]*)$/);

      let optionsFiltered = options || [];
      if (match) {
         const search = match[1].toLowerCase();
         optionsFiltered = options.filter((option) => option.startsWith("@") && option.toLowerCase().includes(search));
      } else if (literalOptions.length) {
         const search = inputValue.toLowerCase();
         optionsFiltered = literalOptions.filter((option) => option.toLowerCase().includes(search));
      }
      optionsFiltered = optionsFiltered.sort((a, b) => a.localeCompare(b));
      return optionsFiltered;
   };

   const setAutocompleteDataSource = () => {
      const stepsPaths = getObjectPaths(context.steps);
      const triggersPaths = getObjectPaths(context.triggers);
      const settingsPaths = getObjectPaths(context.settings);
      const flowConfigurationPaths = getObjectPaths(context.flow_configuration);
      const sharedFlowConfigurationPaths = getObjectPaths(context.shared_flow_configurations);
      const varOptions = getVariablesDS();
      let options = [];

      stepsPaths.forEach((path) => {
         const pathString = `@steps.${path.join(".")}`;
         if (pathString.split(".")[5] === "componentData") return;
         options.push(pathString);
      });

      triggersPaths.forEach((path) => {
         const pathString = `@triggers.${path.join(".")}`;
         options.push(pathString);
      });

      settingsPaths.forEach((path) => {
         const pathString = `@settings.${path.join(".")}`;
         options.push(pathString);
      });

      flowConfigurationPaths.forEach((path) => {
         const pathString = `@flow_configuration.${path.join(".")}`;
         options.push(pathString);
      });

      sharedFlowConfigurationPaths.forEach((path) => {
         const pathString = `@shared_flow_configurations.${path.join(".")}`;
         options.push(pathString);
      });

      const idPath = `@flow_id`;
      const tenantIdPath = `@tenant_id`;
      const integrationIdsPath = `@integration_ids`;
      const flowNamePath = `@flow_name`;
      const invocationPath = `@invocation`;
      const sessionPath = `@session`;

      options = [idPath, tenantIdPath, integrationIdsPath, flowNamePath, invocationPath, sessionPath, ...varOptions, ...options];

      allOptions.current = options;
      setDataSource(options);
   };

   const getVariablesDS = () => {
      const varOptions = [];

      try {
         const varKeys = context?.variables;
         Object.keys(varKeys).map((key) => {
            if (varKeys[key]) {
               if (typeof varKeys[key] === "object") {
                  Object.keys(varKeys[key]).map((varKey) => {
                     varOptions.push(`@variables.${key}.${varKey}`);
                  });
               } else {
                  varOptions.push(`@variables.${key}`);
               }
            } else {
               varOptions.push(`@variables.${key}`);
            }
         });
      } catch (e) {
         console.log("error", e);
      }

      return varOptions;
   };

   const validateEmailInput = (email) => {
      if (email && email[0] === "@") return true;
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(email);
   };

   const handleTextFieldOnChange = (e) => {
      const value = e.target.value;
      const input = inputRef.current?.querySelector("input");
      const cursorPos = input?.selectionStart ?? value.length;
      const atToken = getAtTokenAtCursor(value, cursorPos);

      if (atToken && atToken.includes(".")) {
         const lastDotIndex = atToken.lastIndexOf(".");
         const parentPath = atToken.substring(0, lastDotIndex);
         const childOptions = getChildOptions(parentPath);
         setDataSource(childOptions);
         setOpen(true);
      } else if (atToken && !isBranchInput) {
         setDataSource(allOptions.current);
         setOpen(true);
      } else if (literalOptions.length) {
         setDataSource(literalOptions);
         setOpen(true);
      }

      if (selectedNode.key === COMMON_COMPONENT_TYPES.EMAIL && action.field === "from" && value) {
         const isEmailValid = validateEmailInput(value);

         if (!isEmailValid) {
            setInvalidEmailInput(true);
         } else {
            setInvalidEmailInput(false);
         }
      }

       if (!noValidate) setFormErrors((prev) => prev?.filter((error) => error !== action.field && error !== `invalid_variable_name_${action.field}`));
       if (hasSecondaryInput) {
          setFormErrors((prev) => prev?.filter((error) => error !== `missing_variable_default_${action.parentField}`));
       }

      onChange(e);
   };

   const handleTextFieldBlur = () => {
      if (!noValidate && !fieldValue && action.isRequired) {
         setFormErrors((prev) => (prev.includes(action.field) ? prev : [...prev, action.field]));
      } else {
         setFormErrors((prev) => prev?.filter((error) => error !== action.field));
      }
      if (onBlur) onBlur();
      closeAutocomplete();
   };

   useEffect(() => {
      setAutocompleteDataSource();
   }, [context]);

    return (
      <>
         <Autocomplete
            open={open}
            className={className}
            freeSolo
            options={dataSource}
            inputValue={String(fieldValue ?? "")}
            disabled={disabled}
            renderInput={(params) => (
               <TextField
                  {...params}
                  required={action.isRequired}
                  ref={inputRef}
                  helperText={
                     formErrors.includes(`missing_variable_default_${action.parentField}`)
                        ? `${action.label} is required.`
                        : formErrors.includes(`duplicate_variable_name_${action.field}`)
                           ? "Variable and Output Reference names must be unique."
                           : formErrors.includes(action.field)
                              ? errorHelperText || `${action.label || "Output Reference"} is required.`
                              : invalidEmailInput
                                 ? "Invalid email address"
                                 : ""
                  }
                  multiline={action.multiline || isMultiline}
                   error={formErrors?.includes(`missing_variable_default_${action.parentField}`) || formErrors?.includes(action.field) || formErrors?.includes(`invalid_variable_name_${action.field}`) || formErrors?.includes(`duplicate_variable_name_${action.field}`) || invalidEmailInput}
                  minRows="3"
                  name={action.field}
                  placeholder={action.placeholder || ""}
                  label={hideLabel ? undefined : keyValueInput ? keyValueInput.name : action.label}
                  onChange={handleTextFieldOnChange}
                  onBlur={handleTextFieldBlur}
                  onFocus={handleFocus}
                  sx={{
                     "& .MuiInputLabel-root": {
                        fontSize: "13px"
                     }
                  }}
                  variant="outlined"
                  type="text"
                  disabled={disabled}
               />
            )}
             onChange={handleAutocompleteChange}
             filterOptions={filterAutompleteOptions}
             slotProps={{
               popper: {
                  component: AutocompletePopperComponent
               }
            }}
            selectOnFocus
            handleHomeEndKeys
            renderOption={(props, option) => {
               const { key, ...optionProps } = props;
               return (
                  <SoftTypography key={key} {...optionProps} sx={{ cursor: "pointer" }}>
                     {option}
                  </SoftTypography>
               );
            }}
         />
      </>
   );
};

export default AutocompleteInput;
